<template>
  <div class="message-renderer-demo">
    <h1>统一消息渲染组件测试</h1>
    
    <div class="demo-container">
      <!-- 控制面板 -->
      <div class="control-panel">
        <h3>测试控制</h3>
        
        <div class="control-group">
          <label>消息类型：</label>
          <select v-model="selectedType" @change="loadTestMessage">
            <option value="text">纯文本</option>
            <option value="markdown">Markdown</option>
            <option value="agent">智能体消息</option>
            <option value="code">代码块</option>
            <option value="math">数学公式</option>
            <option value="mermaid">Mermaid图表</option>
            <option value="mixed">混合内容</option>
            <option value="streaming">流式消息</option>
          </select>
        </div>
        
        <div class="control-group">
          <label>
            <input type="checkbox" v-model="enableKatex"> 启用数学公式
          </label>
        </div>
        
        <div class="control-group">
          <label>
            <input type="checkbox" v-model="enableMermaid"> 启用Mermaid图表
          </label>
        </div>
        
        <div class="control-group">
          <label>
            <input type="checkbox" v-model="enableHighlight"> 启用代码高亮
          </label>
        </div>
        
        <div class="control-group" v-if="selectedType === 'streaming'">
          <button @click="startStreaming" :disabled="isStreaming">开始流式传输</button>
          <button @click="stopStreaming" :disabled="!isStreaming">停止流式传输</button>
        </div>
        
        <div class="control-group">
          <label>自定义内容：</label>
          <textarea v-model="customContent" rows="10" cols="50"></textarea>
          <button @click="renderCustomContent">渲染自定义内容</button>
        </div>
      </div>
      
      <!-- 渲染结果 -->
      <div class="render-panel">
        <h3>渲染结果</h3>
        
        <!-- 普通消息渲染 -->
        <div v-if="selectedType !== 'streaming'" class="message-wrapper">
          <unified-message-renderer
            ref="normalRenderer"
            :message="currentMessage"
            :custom-config="rendererConfig"
            @rendered="onRendered"
          />
        </div>
        
        <!-- 流式消息渲染 -->
        <div v-else class="message-wrapper">
          <streaming-renderer
            ref="streamingRenderer"
            :initial-message="currentMessage"
            :custom-config="rendererConfig"
            @streaming-start="onStreamingStart"
            @content-update="onContentUpdate"
            @streaming-finish="onStreamingFinish"
          />
        </div>
        
        <!-- 性能统计 -->
        <div v-if="performanceStats" class="performance-stats">
          <h4>性能统计</h4>
          <pre>{{ JSON.stringify(performanceStats, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UnifiedMessageRenderer from './UnifiedMessageRenderer.vue';
import StreamingRenderer from './StreamingRenderer.vue';

// 测试数据
const TEST_MESSAGES = {
  text: {
    content: '这是一条普通的文本消息。\n包含换行符。\n还有一个链接：https://example.com',
    type: 'text'
  },
  
  markdown: {
    content: `# Markdown 测试

## 二级标题

这是一个包含各种 **Markdown** 语法的测试消息。

### 列表测试
- 无序列表项 1
- 无序列表项 2
  - 嵌套项
  - 另一个嵌套项

1. 有序列表项 1
2. 有序列表项 2

### 任务列表
- [x] 已完成的任务
- [ ] 未完成的任务
- [ ] 另一个待办事项

### 引用
> 这是一段引用文字
> 可以有多行

### 表格
| 列1 | 列2 | 列3 |
|-----|-----|-----|
| A1  | B1  | C1  |
| A2  | B2  | C2  |

### 链接和图片
[这是一个链接](https://example.com)

### 分隔线
---

*斜体文字* 和 **粗体文字** 以及 ***粗斜体***`,
    type: 'markdown',
    format: 'markdown'
  },
  
  agent: {
    content: `我来帮您分析这个问题。

## 分析步骤

1. **问题理解**：首先需要明确问题的核心
2. **方案设计**：基于需求设计解决方案
3. **实施计划**：制定具体的实施步骤

\`\`\`javascript
// 示例代码
function calculateSum(numbers) {
  return numbers.reduce((acc, num) => acc + num, 0);
}

const result = calculateSum([1, 2, 3, 4, 5]);
console.log('Sum:', result); // Output: 15
\`\`\`

### 总结
以上是我的分析和建议，希望对您有所帮助。`,
    type: 'AGENT',
    isAgent: true,
    agentName: 'AI助手'
  },
  
  code: {
    content: `这里是代码示例：

\`\`\`javascript
// JavaScript 代码高亮测试
class MessageRenderer {
  constructor(options) {
    this.options = options;
    this.buffer = '';
  }
  
  render(content) {
    const rendered = this.processContent(content);
    return \`<div class="message">\${rendered}</div>\`;
  }
  
  processContent(content) {
    // 处理内容
    return content
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

const renderer = new MessageRenderer({ markdown: true });
console.log(renderer.render('Hello, World!'));
\`\`\`

\`\`\`python
# Python 代码示例
def fibonacci(n):
    """生成斐波那契数列"""
    if n <= 0:
        return []
    elif n == 1:
        return [0]
    elif n == 2:
        return [0, 1]
    
    fib = [0, 1]
    for i in range(2, n):
        fib.append(fib[-1] + fib[-2])
    
    return fib

print(fibonacci(10))
\`\`\`

还有行内代码：\`const x = 42;\``,
    type: 'markdown',
    format: 'markdown'
  },
  
  math: {
    content: `数学公式测试：

行内公式：$E = mc^2$ 是爱因斯坦的质能方程。

块级公式：
$$
\\begin{align}
\\nabla \\times \\vec{\\mathbf{B}} -\\, \\frac1c\\, \\frac{\\partial\\vec{\\mathbf{E}}}{\\partial t} &= \\frac{4\\pi}{c}\\vec{\\mathbf{j}} \\\\
\\nabla \\cdot \\vec{\\mathbf{E}} &= 4 \\pi \\rho \\\\
\\nabla \\times \\vec{\\mathbf{E}}\\, +\\, \\frac1c\\, \\frac{\\partial\\vec{\\mathbf{B}}}{\\partial t} &= \\vec{\\mathbf{0}} \\\\
\\nabla \\cdot \\vec{\\mathbf{B}} &= 0
\\end{align}
$$

另一个公式：
$$
f(x) = \\int_{-\\infty}^{\\infty} \\hat{f}(\\xi) e^{2\\pi i \\xi x} d\\xi
$$`,
    type: 'markdown',
    format: 'markdown'
  },
  
  mermaid: {
    content: `流程图示例：

\`\`\`mermaid
graph TD
    A[开始] --> B{是否登录?}
    B -->|是| C[进入主页]
    B -->|否| D[显示登录页]
    D --> E[输入账号密码]
    E --> F[验证]
    F -->|成功| C
    F -->|失败| D
    C --> G[结束]
\`\`\`

\`\`\`mermaid
sequenceDiagram
    participant 用户
    participant 前端
    participant 后端
    participant 数据库
    
    用户->>前端: 发送消息
    前端->>后端: POST /api/message
    后端->>数据库: 保存消息
    数据库-->>后端: 返回结果
    后端-->>前端: 响应
    前端-->>用户: 显示结果
\`\`\``,
    type: 'markdown',
    format: 'markdown'
  },
  
  mixed: {
    content: `# 综合内容测试

这是一个包含多种元素的**综合测试**。

## 1. 代码示例

\`\`\`javascript
const greeting = (name) => {
  return \`Hello, \${name}!\`;
};
\`\`\`

## 2. 数学公式

牛顿第二定律：$F = ma$

二次方程的解：
$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$

## 3. 表格

| 功能 | 支持 | 备注 |
|------|------|------|
| Markdown | ✅ | 完整支持 |
| LaTeX | ✅ | KaTeX渲染 |
| 代码高亮 | ✅ | Prism.js |

## 4. 流程图

\`\`\`mermaid
graph LR
    A[输入] --> B[处理]
    B --> C[输出]
\`\`\`

## 5. 任务列表

- [x] 实现基础渲染
- [x] 添加数学公式
- [ ] 性能优化
- [ ] 添加主题

> 💡 **提示**：这个组件支持完整的 GFM (GitHub Flavored Markdown) 语法。

---

*测试完成！*`,
    type: 'AGENT',
    format: 'markdown'
  },
  
  streaming: {
    content: '',
    type: 'STREAM',
    streaming: true
  }
};

// 流式消息测试内容
const STREAMING_CONTENT = `# 流式传输测试

这是一个**流式传输**的测试消息，内容会逐步显示。

## 功能特点

1. 支持增量渲染
2. 智能缓冲优化
3. 性能监控

\`\`\`javascript
// 流式渲染示例
function streamRender(chunks) {
  chunks.forEach((chunk, index) => {
    setTimeout(() => {
      appendContent(chunk);
    }, index * 100);
  });
}
\`\`\`

数学公式也能正常渲染：$E = mc^2$

| 测试项 | 结果 |
|--------|------|
| 渲染速度 | 优秀 |
| 内存占用 | 正常 |

> 流式传输让用户体验更加流畅！

测试完成。`;

export default {
  name: 'MessageRendererDemo',
  
  components: {
    UnifiedMessageRenderer,
    StreamingRenderer
  },
  
  data() {
    return {
      selectedType: 'markdown',
      currentMessage: TEST_MESSAGES.markdown,
      customContent: '',
      
      // 配置选项
      enableKatex: true,
      enableMermaid: true,
      enableHighlight: true,
      
      // 流式相关
      isStreaming: false,
      streamingChunks: [],
      streamingIndex: 0,
      streamingTimer: null,
      
      // 性能统计
      performanceStats: null
    };
  },
  
  computed: {
    rendererConfig() {
      return {
        katex: this.enableKatex,
        mermaid: this.enableMermaid,
        highlight: this.enableHighlight
      };
    }
  },
  
  methods: {
    loadTestMessage() {
      this.currentMessage = TEST_MESSAGES[this.selectedType] || TEST_MESSAGES.text;
      this.performanceStats = null;
    },
    
    renderCustomContent() {
      if (!this.customContent) {
        alert('请输入自定义内容');
        return;
      }
      
      this.currentMessage = {
        content: this.customContent,
        type: 'CUSTOM',
        format: 'markdown'
      };
    },
    
    // 流式传输相关
    startStreaming() {
      if (this.isStreaming) return;
      
      this.isStreaming = true;
      this.streamingChunks = this.splitIntoChunks(STREAMING_CONTENT, 10);
      this.streamingIndex = 0;
      
      // 开始流式传输
      this.$refs.streamingRenderer.startStreaming();
      
      // 模拟流式数据
      this.streamingTimer = setInterval(() => {
        if (this.streamingIndex >= this.streamingChunks.length) {
          this.stopStreaming();
          return;
        }
        
        const chunk = this.streamingChunks[this.streamingIndex++];
        this.$refs.streamingRenderer.appendChunk(chunk);
      }, 100);
    },
    
    stopStreaming() {
      if (!this.isStreaming) return;
      
      clearInterval(this.streamingTimer);
      this.isStreaming = false;
      
      if (this.$refs.streamingRenderer) {
        this.$refs.streamingRenderer.finishStreaming();
      }
    },
    
    splitIntoChunks(text, chunkSize) {
      const chunks = [];
      for (let i = 0; i < text.length; i += chunkSize) {
        chunks.push(text.slice(i, i + chunkSize));
      }
      return chunks;
    },
    
    // 事件处理
    onRendered() {
      console.log('消息渲染完成');
    },
    
    onStreamingStart() {
      console.log('流式传输开始');
      this.performanceStats = null;
    },
    
    onContentUpdate(content) {
      console.log('内容更新，长度:', content.length);
    },
    
    onStreamingFinish(data) {
      console.log('流式传输完成', data);
      this.performanceStats = data.stats;
    }
  },
  
  mounted() {
    // 初始加载
    this.loadTestMessage();
  },
  
  beforeDestroy() {
    // 清理定时器
    if (this.streamingTimer) {
      clearInterval(this.streamingTimer);
    }
  }
};
</script>

<style lang="scss" scoped>
.message-renderer-demo {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  
  h1 {
    color: #333;
    border-bottom: 2px solid #0066cc;
    padding-bottom: 10px;
    margin-bottom: 20px;
  }
  
  .demo-container {
    display: flex;
    gap: 20px;
    
    .control-panel {
      flex: 0 0 400px;
      background: #f5f5f5;
      padding: 20px;
      border-radius: 8px;
      
      h3 {
        margin-top: 0;
        color: #333;
      }
      
      .control-group {
        margin-bottom: 15px;
        
        label {
          display: block;
          margin-bottom: 5px;
          color: #666;
        }
        
        select, textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        button {
          padding: 8px 16px;
          background: #0066cc;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          margin-right: 10px;
          
          &:hover {
            background: #0052a3;
          }
          
          &:disabled {
            background: #ccc;
            cursor: not-allowed;
          }
        }
        
        input[type="checkbox"] {
          margin-right: 5px;
        }
      }
    }
    
    .render-panel {
      flex: 1;
      
      h3 {
        margin-top: 0;
        color: #333;
      }
      
      .message-wrapper {
        background: white;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 20px;
        min-height: 300px;
        max-height: 600px;
        overflow-y: auto;
      }
      
      .performance-stats {
        margin-top: 20px;
        background: #f9f9f9;
        border: 1px solid #e1e4e8;
        border-radius: 4px;
        padding: 15px;
        
        h4 {
          margin-top: 0;
          color: #666;
        }
        
        pre {
          background: white;
          padding: 10px;
          border-radius: 4px;
          overflow-x: auto;
          font-size: 12px;
        }
      }
    }
  }
}

// 响应式布局
@media (max-width: 1024px) {
  .message-renderer-demo {
    .demo-container {
      flex-direction: column;
      
      .control-panel {
        flex: none;
        width: 100%;
      }
    }
  }
}
</style>